import { getCurrentUser as amplifyGetCurrentUser, signOut } from 'aws-amplify/auth';
import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ApiSystem, User } from "../state/types";
import { normalizeSystemsData } from '../components/system/systemHelpers';
import { userHasAccess } from '../services/RoleBasedAccess';
import { useSystemsApi } from '../api/useSystemsApi';
import { useAccountApi } from '../api/useAccountApi';
import { getRawAuthToken, setUnauthorizedHandler, SESSION_EXPIRED_MESSAGE } from '../api/apiClient';
import { stripUnitSuffix } from '../utils/systemCode';
import {
  LOGOUT_MESSAGE_KEY,
  RELEASE_ID,
  RELEASE_STORAGE_KEY,
  RELEASE_VERSION,
  RELEASED_AT,
  formatReleaseDate,
} from '../config/release';

const PUBLIC_PATHS = ['/login', '/logout', '/resetPassword'];

type InitializeAppOptions = {
  forceRefetch?: boolean;
  persistReleaseIdOnSuccess?: boolean;
  skipReleaseValidation?: boolean;
};

export const useSessionState = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [user, setUser] = useState<User | null>(() => {
    const saved = localStorage.getItem('user');
    return saved ? JSON.parse(saved) : null;
  });
  const [originalUser, setOriginalUser] = useState<User | null>(() => {
    const saved = localStorage.getItem('originalUser');
    return saved ? JSON.parse(saved) : null;
  });
  const [systems, setSystems] = useState<ApiSystem[]>(() => {
    const saved = localStorage.getItem('systems');
    return saved ? JSON.parse(saved) : [];
  });
  const [selectedSystem, setSelectedSystem] = useState<ApiSystem | null>(() => {
    // Hydrate the selected system from cache so warm boots paint instantly,
    // before any network call resolves. initializeApp revalidates after.
    try {
      const savedUser = localStorage.getItem('user');
      const savedSystems = localStorage.getItem('systems');
      if (!savedUser || !savedSystems) return null;
      const cachedUser = JSON.parse(savedUser) as User;
      const cachedSystems = JSON.parse(savedSystems) as ApiSystem[];
      const savedCode = stripUnitSuffix(localStorage.getItem(`selectedSystem:${cachedUser.username}`));
      return cachedSystems.find(s => s.systemCode === savedCode) || cachedSystems[0] || null;
    } catch {
      return null;
    }
  });
  const [assetFilterResetToken, setAssetFilterResetToken] = useState(0);
  const [loadingCount, setLoadingCount] = useState(0);
  const isLoading = loadingCount > 0;
  const setIsLoading = useCallback((loading: boolean) => {
    setLoadingCount((current) => {
      if (loading) return current + 1;
      return Math.max(0, current - 1);
    });
  }, []);

  const { getSystemsApi, getSystemApi } = useSystemsApi();
  const { loginUserApi } = useAccountApi();
  const selectedSystemRef = useRef<ApiSystem | null>(selectedSystem);
  const userRef = useRef<User | null>(user);
  const systemsRef = useRef<ApiSystem[]>(systems);
  // Read pathname through a ref inside initializeApp so route changes don't
  // recreate the callback (which previously re-fired the boot on every nav).
  const locationPathnameRef = useRef(location.pathname);
  // Dedupe redundant background boots; forced boots (login/masquerade) bypass it.
  const bootInFlightRef = useRef<Promise<void> | null>(null);
  // Coalesce concurrent full-list fetches into a single network call, so two
  // boots firing at startup (mount effect + explicit login) don't each scan.
  const fullFetchPromiseRef = useRef<Promise<ApiSystem[]> | null>(null);
  // Timestamp of the last completed full fetch, so a second boot a moment later
  // skips a redundant background refresh (the list was just pulled).
  const lastFullFetchAtRef = useRef(0);

  const handleLogoutCleanup = useCallback(() => {
    setUser(null);
    setOriginalUser(null);
    setSystems([]);
    setSelectedSystem(null);
    localStorage.removeItem('user');
    localStorage.removeItem('systems');
    localStorage.removeItem('originalUser');
    localStorage.removeItem('impersonatedUser');
    localStorage.removeItem('assetsNavState');
    localStorage.removeItem('vehiclesNavState');
    localStorage.removeItem(RELEASE_STORAGE_KEY);
  }, []);

  const persistReleaseId = useCallback(() => {
    if (!RELEASE_ID) return;
    localStorage.setItem(RELEASE_STORAGE_KEY, RELEASE_ID);
  }, []);

  const logoutWithReason = useCallback(async (reason: string) => {
    sessionStorage.setItem(LOGOUT_MESSAGE_KEY, reason);
    try {
      await signOut();
    } catch (error) {
      console.error('Error signing out: ', error);
    } finally {
      handleLogoutCleanup();
      navigate('/login', { replace: true });
    }
  }, [handleLogoutCleanup, navigate]);

  const logoutForReleaseChange = useCallback(async () => {
    const releaseDetails = RELEASED_AT
      ? `version ${RELEASE_VERSION}, released ${formatReleaseDate(RELEASED_AT)}`
      : `version ${RELEASE_VERSION}`;

    await logoutWithReason(
      `The app was updated to ${releaseDetails}. Please sign in again to load the latest release.`
    );
  }, [logoutWithReason]);

  // Full-list fetch with in-flight coalescing: concurrent callers share one
  // network call instead of each kicking off their own table scan.
  const fetchFullSystems = useCallback(() => {
    if (fullFetchPromiseRef.current) return fullFetchPromiseRef.current;
    const p = getSystemsApi()
      .then((systems) => {
        lastFullFetchAtRef.current = Date.now();
        return systems;
      })
      .finally(() => {
        if (fullFetchPromiseRef.current === p) fullFetchPromiseRef.current = null;
      });
    fullFetchPromiseRef.current = p;
    return p;
  }, [getSystemsApi]);

  const doInitialize = useCallback(async ({
    forceRefetch = false,
    persistReleaseIdOnSuccess = false,
    skipReleaseValidation = false,
  }: InitializeAppOptions = {}) => {
    const isIframePath = locationPathnameRef.current.toLowerCase().includes('/iframe');

    setIsLoading(true);
    const hasStaleUser = !!localStorage.getItem('user');
    const isMasquerading = !!localStorage.getItem('originalUser');

    try {
      if (isIframePath) {
        return;
      }

      let currentUser: User | null = userRef.current;

      // --- IDENTITY PHASE ---
      if (isMasquerading) {
        // Trust storage for Masquerade; skip API to avoid reverting to Admin data
        const storedImpersonated = localStorage.getItem('impersonatedUser');
        if (storedImpersonated) currentUser = JSON.parse(storedImpersonated);
        await amplifyGetCurrentUser(); // Ensure underlying admin session is alive
      } else {
        // Normal flow: only call API if we don't have a user, or in background
        if (!hasStaleUser || forceRefetch) {
          await amplifyGetCurrentUser();
          currentUser = await loginUserApi();
          setUser(currentUser);
          localStorage.setItem('user', JSON.stringify(currentUser));
        }
      }

      const storedReleaseId = localStorage.getItem(RELEASE_STORAGE_KEY);
      if (
        currentUser &&
        RELEASE_ID &&
        !skipReleaseValidation &&
        storedReleaseId !== RELEASE_ID
      ) {
        await logoutForReleaseChange();
        return;
      }

      if (currentUser && RELEASE_ID && persistReleaseIdOnSuccess) {
        persistReleaseId();
      }

      // --- SYSTEMS PHASE ---
      // The full list comes from an expensive table scan, so we never block the
      // UI on it when we already have a cached list: paint the selection from
      // cache immediately and refresh in the background. Per-system freshness on
      // the Systems page is handled by refetchSelectedSystem (a cheap get_item)
      // + IoT — not here, so boot stays to a single (coalesced) list fetch.
      if (currentUser) {
        const sessionUser = currentUser;
        const storageKey = `selectedSystem:${sessionUser.username}`;
        const savedCode = stripUnitSuffix(localStorage.getItem(storageKey));

        // Pick the best selection out of a list (current > saved > first).
        const pickSelection = (list: ApiSystem[]): ApiSystem | null => {
          const currentCode = stripUnitSuffix(selectedSystemRef.current?.systemCode || "");
          return (
            list.find(s => s.systemCode === currentCode) ||
            list.find(s => s.systemCode === savedCode) ||
            list[0] ||
            null
          );
        };

        const applySelection = (next: ApiSystem | null) => {
          setSelectedSystem(next);
          selectedSystemRef.current = next; // sync now, not on the next render
          if (next) localStorage.setItem(storageKey, next.systemCode);
        };

        // Normalize + masquerade-filter a fetched list, persist it, reconcile.
        const reconcileAndStore = (rawSystems: ApiSystem[]): ApiSystem[] => {
          let finalSystems = normalizeSystemsData(rawSystems);
          if (isMasquerading && !userHasAccess(sessionUser.role, ['Internal'])) {
            const accessibleCodes = new Set((sessionUser.system_access || []).map((code: string) => stripUnitSuffix(code)));
            finalSystems = finalSystems.filter(s => accessibleCodes.has(s.systemCode));
          }
          setSystems(finalSystems);
          systemsRef.current = finalSystems; // sync now so back-to-back boots see cache
          localStorage.setItem('systems', JSON.stringify(finalSystems));
          applySelection(pickSelection(finalSystems));
          return finalSystems;
        };

        const haveCache = systemsRef.current.length > 0 && !isMasquerading;
        if (haveCache) {
          // Warm boot — select from cache instantly (no spinner), refresh behind.
          applySelection(pickSelection(systemsRef.current));
          // Skip the background refresh if the list was just pulled (e.g. the
          // mount boot already refreshed moments before this login boot).
          const FRESH_MS = 15000;
          if (Date.now() - lastFullFetchAtRef.current > FRESH_MS) {
            fetchFullSystems()
              .then((raw) => reconcileAndStore(raw))
              .catch((err) => console.error("Background systems refresh failed:", err));
          }
        } else {
          // Cold boot or masquerade — must fetch to render. Coalesced so
          // concurrent boots share one network call rather than each scanning.
          reconcileAndStore(await fetchFullSystems());
        }
      }
    } catch (error) {
      console.error("Critical Boot Error:", error);
      handleLogoutCleanup();
    } finally {
      setIsLoading(false);
    }
  }, [fetchFullSystems, handleLogoutCleanup, loginUserApi, logoutForReleaseChange, persistReleaseId, setIsLoading]);

  // Dedupe concurrent boots so an accidental double-invoke (or overlapping
  // background refresh) doesn't fire the boot sequence twice. Forced boots
  // (login, masquerade) always run.
  const initializeApp = useCallback((options: InitializeAppOptions = {}) => {
    if (!options.forceRefetch && bootInFlightRef.current) {
      return bootInFlightRef.current;
    }
    const run = doInitialize(options).finally(() => {
      if (bootInFlightRef.current === run) bootInFlightRef.current = null;
    });
    bootInFlightRef.current = run;
    return run;
  }, [doInitialize]);

  useEffect(() => {
    initializeApp();
  }, [initializeApp]);

  useEffect(() => {
    locationPathnameRef.current = location.pathname;
  }, [location.pathname]);

  // Register a global handler the apiClient can call when it detects a dead
  // session (e.g. the 401 retry also fails). We tear it down on unmount so
  // tests and HMR don't leak handlers across renders.
  useEffect(() => {
    setUnauthorizedHandler((reason) => {
      logoutWithReason(reason);
    });
    return () => setUnauthorizedHandler(null);
  }, [logoutWithReason]);

  // Proactive stale-session probe: when the tab regains focus after being
  // hidden, force-refresh the Cognito session via getRawAuthToken. We use
  // getRawAuthToken rather than fetchAuthSession directly because Amplify v6
  // returns an empty session object (no throw) when tokens are missing — we
  // need the helper's "No idToken available" throw to detect that case along
  // with refresh-token failures. If it throws, the user would hit a 401 on
  // their next click anyway — log them out now with a friendly message.
  useEffect(() => {
    const probe = async () => {
      if (document.visibilityState !== 'visible') return;
      if (!userRef.current) return;
      const isPublicPath = PUBLIC_PATHS.some(path => location.pathname.startsWith(path));
      const isIframePath = location.pathname.toLowerCase().includes('/iframe');
      if (isPublicPath || isIframePath) return;

      try {
        await getRawAuthToken();
      } catch (error) {
        console.warn('Visibility-change session probe failed; logging out.', error);
        await logoutWithReason(SESSION_EXPIRED_MESSAGE);
      }
    };

    document.addEventListener('visibilitychange', probe);
    return () => document.removeEventListener('visibilitychange', probe);
  }, [location.pathname, logoutWithReason]);

  useEffect(() => {
    selectedSystemRef.current = selectedSystem;
  }, [selectedSystem]);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    systemsRef.current = systems;
  }, [systems]);

  useEffect(() => {
    if (isLoading) return;

    const isPublicPath = PUBLIC_PATHS.some(path => location.pathname.startsWith(path));
    const isIframePath = location.pathname.toLowerCase().includes('iframe');

    if (!user && !isPublicPath && !isIframePath) {
      navigate('/login');
    }
  }, [user, isLoading, navigate, location.pathname]);

  const logoutUser = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Error signing out: ', error);
    } finally {
      handleLogoutCleanup();
      navigate('/login', { replace: true });
    }
  };

  const startMasquerade = (impersonatedUser: User) => {
    if (user && userHasAccess(user?.role, ['Admin', 'Support'])) {
      setOriginalUser(user);
      setUser(impersonatedUser);
      localStorage.setItem('user', JSON.stringify(impersonatedUser));
      localStorage.setItem('originalUser', JSON.stringify(user));
      localStorage.setItem('impersonatedUser', JSON.stringify(impersonatedUser));

      initializeApp({ forceRefetch: true });
    }
  };

  const stopMasquerade = () => {
    setUser(originalUser);
    setOriginalUser(null);
    localStorage.removeItem('originalUser');
    localStorage.removeItem('impersonatedUser');

    initializeApp({ forceRefetch: true });
  };

  const clearAssetNavStateAndNavigate = useCallback((path: string) => {
    console.log("clearing nav state")
    localStorage.removeItem('assetsNavState');
    navigate(path);
  }, [navigate]);

  const selectSystem = useCallback((sys: ApiSystem | null, options?: { skipNavigation?: boolean }) => {
    setSelectedSystem(sys);
    localStorage.removeItem('assetsNavState');
    localStorage.removeItem('vehiclesNavState');
    setAssetFilterResetToken((t) => t + 1);
    if (!user) return;
    const key = `selectedSystem:${user.username}`;
    if (sys) localStorage.setItem(key, sys.systemCode);
    else localStorage.removeItem(key);
    if (!options?.skipNavigation && location.pathname.startsWith('/asset/')) {
      clearAssetNavStateAndNavigate('/assets');
    }
  }, [clearAssetNavStateAndNavigate, location.pathname, user]);

  const loginUser = useCallback(() => initializeApp({
    forceRefetch: true,
    persistReleaseIdOnSuccess: true,
    skipReleaseValidation: true,
  }), [initializeApp]);

  /**
   * Refresh only the systems list from the API — does NOT re-verify Cognito
   * identity or re-fetch the user. Use this when the user is already
   * authenticated and you just want to pick up system changes (e.g., after
   * creating/editing/deleting a system, or when navigating to the Systems page).
   *
   * For a full identity + systems refresh (e.g., after login), use loginUser.
   */
  const refetchSystems = useCallback(async () => {
    if (!userRef.current) return;
    setIsLoading(true);
    try {
      const rawSystems = await fetchFullSystems();
      const finalSystems = normalizeSystemsData(rawSystems);
      setSystems(finalSystems);
      systemsRef.current = finalSystems;
      localStorage.setItem('systems', JSON.stringify(finalSystems));

      // Re-apply selection logic in case the currently selected system was removed
      const storageKey = `selectedSystem:${userRef.current.username}`;
      const savedCode = stripUnitSuffix(localStorage.getItem(storageKey));
      const currentCode = stripUnitSuffix(selectedSystemRef.current?.systemCode || "");

      const next =
        finalSystems.find(s => s.systemCode === currentCode) ||
        finalSystems.find(s => s.systemCode === savedCode) ||
        finalSystems[0] ||
        null;

      setSelectedSystem(next);
      selectedSystemRef.current = next;
      if (next) localStorage.setItem(storageKey, next.systemCode);
    } catch (error) {
      console.error("refetchSystems error:", error);
      // Don't logout on a failed systems refresh — just log the error.
      // The user still has cached systems and a valid session.
    } finally {
      setIsLoading(false);
    }
  }, [fetchFullSystems, setIsLoading]);

  /**
   * Refresh a SINGLE system via a cheap get_item (not the full table scan).
   * Use this on system selection: the UI already shows the cached copy, so we
   * revalidate just the one the user is looking at and merge the fresh record
   * back in — no spinner, no full list refetch. IoT keeps it live afterward.
   * Defaults to the currently-selected system when no code is passed.
   */
  const refetchSelectedSystem = useCallback(async (systemCode?: string) => {
    const code = stripUnitSuffix(systemCode || selectedSystemRef.current?.systemCode || "");
    if (!userRef.current || !code) return;
    try {
      const fresh = normalizeSystemsData([await getSystemApi(code)])[0];
      if (!fresh) return;

      // Merge the fresh record into the cached list (replace or insert).
      const nextSystems = (() => {
        const others = systemsRef.current.filter(s => s.systemCode !== fresh.systemCode);
        return [...others, fresh].sort((a, b) => a.systemCode.localeCompare(b.systemCode));
      })();
      setSystems(nextSystems);
      systemsRef.current = nextSystems;
      localStorage.setItem('systems', JSON.stringify(nextSystems));

      // Only update the selection if it still points at this system.
      if (stripUnitSuffix(selectedSystemRef.current?.systemCode || "") === fresh.systemCode) {
        setSelectedSystem(fresh);
        selectedSystemRef.current = fresh;
      }
    } catch (error) {
      // A failed single-system refresh is non-fatal: keep the cached copy.
      console.error("refetchSelectedSystem error:", error);
    }
  }, [getSystemApi]);

  return {
    // User Session
    user,
    originalUser,
    isLoggedIn: !!user,
    isLoading,
    setIsLoading,
    loginUser,
    logoutUser,
    startMasquerade,
    stopMasquerade,
    handleNavigation: clearAssetNavStateAndNavigate,

    // Systems storage
    systems,
    setSystems,
    selectedSystem,
    selectSystem,
    refetchSystems,
    refetchSelectedSystem,

    // Asset filter reset signal — bumped on user-initiated system switch
    assetFilterResetToken,
  };
};
